import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { Header } from "@/components/layout/Header";
import { StepIndicator } from "@/components/layout/StepIndicator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  User,
  Stethoscope,
  FileText,
  Cloud,
  Mail,
  Send,
  Check,
  Loader2,
  Download,
  Printer,
  Share2,
  Building2,
  Shield,
  CheckCircle2,
  ArrowRight,
  Home
} from "lucide-react";

// Export destinations
const exportDestinations = [
  {
    id: "patient",
    name: "Paziente",
    description: "Invia i risultati via email al paziente",
    icon: User,
    color: "text-primary",
    bgColor: "bg-primary/10"
  },
  {
    id: "doctor",
    name: "Medico di Base",
    description: "Condividi con il medico curante del paziente",
    icon: Stethoscope,
    color: "text-info",
    bgColor: "bg-info/10"
  },
  {
    id: "fascicolo",
    name: "Fascicolo Sanitario",
    description: "Carica nel Fascicolo Sanitario Elettronico",
    icon: FileText,
    color: "text-secondary",
    bgColor: "bg-secondary/10"
  },
  {
    id: "cloud",
    name: "Cloud Farmacia",
    description: "Salva nel sistema cloud della farmacia",
    icon: Cloud,
    color: "text-accent",
    bgColor: "bg-accent/10"
  }
];

export const ExportPage = () => {
  const navigate = useNavigate();
  const { selectedPatient, sessionResults, resetSession } = useAppContext();
  const [selectedDestinations, setSelectedDestinations] = useState(["cloud"]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const [exportedTo, setExportedTo] = useState([]);

  const toggleDestination = (id) => {
    setSelectedDestinations(prev => 
      prev.includes(id)
        ? prev.filter(d => d !== id)
        : [...prev, id]
    );
  };

  const handleExport = async () => {
    if (selectedDestinations.length === 0) {
      toast.error("Seleziona almeno una destinazione");
      return;
    }

    setIsExporting(true);
    
    // Simulate export to each destination
    for (const destId of selectedDestinations) {
      const dest = exportDestinations.find(d => d.id === destId);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setExportedTo(prev => [...prev, destId]);
      toast.success(`Esportato a: ${dest.name}`);
    }
    
    setIsExporting(false);
    setExportComplete(true);
    
    toast.success("Esportazione completata!", {
      description: "Tutti i risultati sono stati inviati correttamente"
    });
  };

  const handleNewSession = () => {
    resetSession();
    navigate("/patients");
  };

  const handleDownloadPDF = () => {
    toast.success("Download PDF avviato", {
      description: "Il referto verrà scaricato a breve"
    });
  };

  const handlePrint = () => {
    toast.success("Stampa avviata", {
      description: "Il referto verrà stampato"
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Esportazione" />
      <StepIndicator currentStep={5} />
      
      <main className="flex-1 px-4 pb-6 md:px-6">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Patient & session info */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">{selectedPatient?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {sessionResults?.results?.length || 0} parametri rilevati
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="gap-2">
                    <Download className="h-4 w-4" />
                    PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
                    <Printer className="h-4 w-4" />
                    Stampa
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reporting routing info (if set on /reporting-routing) */}
          {sessionResults?.reporting && (
            <Card className="bg-cyan-50/40 border-cyan-200" data-testid="export-routing-info">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-cyan-500 text-white flex items-center justify-center shrink-0">
                    <Send className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">Refertazione instradata</p>
                    <p className="font-semibold text-sm">{sessionResults.reporting.structureName}</p>
                    <p className="text-xs text-muted-foreground">
                      {sessionResults.reporting.specialistName || "Specialista: assegnazione automatica"}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="bg-white border-cyan-300 text-cyan-800">
                  {sessionResults.reporting.services.length} esami
                </Badge>
              </CardContent>
            </Card>
          )}

          {/* Page header */}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Esporta Risultati
            </h1>
            <p className="mt-1 text-muted-foreground">
              Scegli dove inviare i risultati degli esami
            </p>
          </div>

          {/* Export success state */}
          {exportComplete ? (
            <Card className="border-success/50 bg-success/5">
              <CardContent className="py-12">
                <div className="flex flex-col items-center text-center">
                  <div className="h-16 w-16 rounded-full bg-success/20 flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-8 w-8 text-success" />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">Esportazione Completata!</h2>
                  <p className="text-muted-foreground mb-6">
                    I risultati sono stati inviati correttamente a:
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mb-8">
                    {exportedTo.map(destId => {
                      const dest = exportDestinations.find(d => d.id === destId);
                      const Icon = dest.icon;
                      return (
                        <Badge key={destId} variant="outline" className={cn("gap-1.5 py-1.5 px-3", dest.bgColor)}>
                          <Icon className={cn("h-4 w-4", dest.color)} />
                          {dest.name}
                        </Badge>
                      );
                    })}
                  </div>
                  <Button
                    variant="premium"
                    size="lg"
                    onClick={handleNewSession}
                    className="gap-2"
                  >
                    <Home className="h-5 w-5" />
                    Nuova Sessione
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Destination selection */}
              <div className="grid gap-4 md:grid-cols-2">
                {exportDestinations.map((dest, index) => {
                  const Icon = dest.icon;
                  const isSelected = selectedDestinations.includes(dest.id);
                  const isExported = exportedTo.includes(dest.id);
                  
                  return (
                    <Card
                      key={dest.id}
                      className={cn(
                        "cursor-pointer transition-all animate-fade-in-up",
                        isSelected && !isExported && "border-primary ring-2 ring-primary/20",
                        isExported && "border-success bg-success/5",
                        !isSelected && !isExported && "hover:border-primary/50"
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                      onClick={() => !isExporting && !isExported && toggleDestination(dest.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <Checkbox
                            checked={isSelected || isExported}
                            disabled={isExporting || isExported}
                            className="mt-1 h-5 w-5"
                          />
                          <div className={cn("p-2 rounded-lg", dest.bgColor)}>
                            <Icon className={cn("h-5 w-5", dest.color)} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{dest.name}</h3>
                              {isExported && (
                                <Badge variant="outline" className="bg-success/10 text-success border-success/20 gap-1">
                                  <Check className="h-3 w-3" />
                                  Inviato
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {dest.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Privacy notice */}
              <Card className="bg-muted/50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Privacy e Sicurezza</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        I dati vengono trasmessi in modo sicuro e criptato. 
                        Il trattamento avviene nel rispetto del GDPR e delle normative sanitarie vigenti.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Export button */}
              <Card className="sticky bottom-4 shadow-lg border-2">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={selectedDestinations.length > 0 ? "default" : "secondary"}>
                          {selectedDestinations.length} destinazioni
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedDestinations.length === 0 
                          ? "Seleziona almeno una destinazione"
                          : `Invia a: ${selectedDestinations.map(id => 
                              exportDestinations.find(d => d.id === id)?.name
                            ).join(", ")}`
                        }
                      </p>
                    </div>
                    <Button
                      variant="premium"
                      size="lg"
                      onClick={handleExport}
                      disabled={selectedDestinations.length === 0 || isExporting}
                      className="gap-2"
                    >
                      {isExporting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Esportazione...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          Esporta Risultati
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ExportPage;